import React, { useState } from 'react';
import { Scissors, Download, Loader2, ArrowLeft, File } from 'lucide-react';
import { Link } from 'react-router-dom';
import { splitPDF, getPageCount } from '../utils/pdfTools';

const SplitPdf = () => {
    const [file, setFile] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [selectedPages, setSelectedPages] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'application/pdf') {
            setFile(selectedFile);
            const count = await getPageCount(selectedFile);
            setPageCount(count);
            setSelectedPages(`1-${count}`);
        }
    };

    const parsePageSelection = (selection, max) => {
        const pages = new Set();
        const parts = selection.split(',');

        parts.forEach(part => {
            const range = part.trim().split('-');
            if (range.length === 2) {
                const start = parseInt(range[0]);
                const end = parseInt(range[1]);
                if (!isNaN(start) && !isNaN(end)) {
                    for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
                        if (i >= 1 && i <= max) pages.add(i - 1); // 0-indexed
                    }
                }
            } else {
                const page = parseInt(part);
                if (!isNaN(page) && page >= 1 && page <= max) {
                    pages.add(page - 1);
                }
            }
        });

        return Array.from(pages).sort((a, b) => a - b);
    };

    const handleSplit = async () => {
        if (!file) return;

        setIsProcessing(true);
        try {
            const indices = parsePageSelection(selectedPages, pageCount);
            if (indices.length === 0) {
                alert('Please select valid pages.');
                setIsProcessing(false);
                return;
            }

            const splitBlob = await splitPDF(file, indices);
            const url = URL.createObjectURL(splitBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `split_${file.name}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error splitting PDF:', error);
            alert('Failed to split PDF. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="container animate-fade-in">
            <div style={{ marginBottom: '2rem' }}>
                <Link to="/tools/pdf" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
                    <ArrowLeft size={16} /> Back to PDF Tools
                </Link>
            </div>

            <header className="flex-center" style={{ flexDirection: 'column', marginBottom: '3rem' }}>
                <div className="flex-center" style={{
                    width: '64px',
                    height: '64px',
                    background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))',
                    borderRadius: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)'
                }}>
                    <Scissors size={32} />
                </div>
                <h1>Extract Pages</h1>
                <p>Extract specific pages from your PDF file</p>
            </header>

            <main style={{ maxWidth: '800px', margin: '0 auto' }}>
                {!file ? (
                    <div className="glass-card" style={{ marginBottom: '2rem', textAlign: 'center', borderStyle: 'dashed', borderWidth: '2px' }}>
                        <input
                            type="file"
                            id="file-upload"
                            accept=".pdf"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="file-upload" className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            Select PDF File
                        </label>
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>or drop file here</p>
                    </div>
                ) : (
                    <div className="glass-card" style={{ marginBottom: '2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem', padding: '1rem', background: 'rgba(255,255,255,0.5)', borderRadius: '0.5rem' }}>
                            <div style={{ color: 'var(--primary)' }}><File size={32} /></div>
                            <div>
                                <h3 style={{ marginBottom: '0.25rem' }}>{file.name}</h3>
                                <p style={{ fontSize: '0.9rem' }}>{pageCount} pages • {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                                onClick={() => { setFile(null); setPageCount(0); }}
                                className="btn btn-secondary"
                                style={{ marginLeft: 'auto', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                            >
                                Change File
                            </button>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Pages to Extract</label>
                            <input
                                type="text"
                                value={selectedPages}
                                onChange={(e) => setSelectedPages(e.target.value)}
                                placeholder="e.g. 1-5, 8, 11-13"
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border)',
                                    fontSize: '1rem',
                                    fontFamily: 'inherit'
                                }}
                            />
                            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                Enter page numbers and/or ranges separated by commas.
                            </p>
                        </div>

                        <div className="flex-center">
                            <button
                                className="btn btn-primary"
                                onClick={handleSplit}
                                disabled={isProcessing}
                                style={{ padding: '1rem 2rem', fontSize: '1.1rem', width: '100%' }}
                            >
                                {isProcessing ? (
                                    <>
                                        <Loader2 className="animate-spin" /> Extracting Pages...
                                    </>
                                ) : (
                                    <>
                                        <Download /> Extract Pages
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default SplitPdf;
